import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, conversationApi, formatIst, formatIstLongDate, formatIstTime, inboxApi, useAuth,
  type Agent, type AppliedTag, type Collaborator, type ContactField, type Conversation, type Note,
  type ScheduledMessage,
} from '@wa/shared';
import { Avatar } from '@/components/Avatar';
import { TagEditor } from '@/components/TagEditor';
import { NoteComposer } from '@/components/NoteComposer';

interface Props {
  conversation: Conversation;
  agents: Agent[];
  notes: Note[];
  scheduled: ScheduledMessage[];
  onViewScheduled: () => void;
  onClose?: () => void;
  /** Notes are writable from here too — the thread's composer is gone once the
      conversation is closed, and closing is when the last note gets written. */
  onAddNote: (body: string, mentionedAgentIds: number[]) => void;
  isSavingNote: boolean;
}

type Tab = 'details' | 'calls' | 'notes';

/**
 * The right-hand panel.
 *
 * Three tabs, matching the screen being replicated: Details, Calls, Notes. The
 * Calls tab is empty and says why — the Business Calling API needs a 2,000/day
 * unique-recipient tier that none of our numbers holds, and a tab that explains
 * its own blocker is not the same thing as a dead control, which the build
 * reference has forbidden since M1.
 */
export function ContactPanel({
  conversation, agents, notes, scheduled, onViewScheduled, onClose,
  onAddNote, isSavingNote,
}: Props) {
  const [tab, setTab] = useState<Tab>('details');

  return (
    <aside className="flex h-full min-h-0 flex-col border-(--color-line) bg-(--color-surface) xl:border-l">
      <header className="flex shrink-0 items-center border-b border-(--color-line)">
        {/* A tab strip, not a heading with tabs under it. The panel is narrow and a
            title that only ever says "Contact" spends a row saying nothing. */}
        <nav role="tablist" aria-label="Contact details" className="flex min-w-0 flex-1">
          {([
            ['details', 'Details', undefined],
            ['calls', 'Calls', undefined],
            ['notes', 'Notes', notes.length || undefined],
          ] as const).map(([value, label, badge]) => {
            const active = tab === value;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(value)}
                className={[
                  'tap relative flex min-w-0 flex-1 items-center justify-center gap-1.5 px-2 py-3 text-body font-normal transition-colors',
                  active
                    ? 'text-(--color-brand) after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full after:bg-(--color-brand)'
                    : 'text-(--color-muted) hover:text-(--color-ink)',
                ].join(' ')}
              >
                <span className="truncate">{label}</span>
                {badge !== undefined && (
                  <span className="rounded-full bg-(--color-sunken) px-1.5 text-micro tabular-nums text-(--color-muted)">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="tap grid shrink-0 place-items-center px-3 text-(--color-muted) hover:text-(--color-ink)"
            aria-label="Close contact details"
          >
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </header>

      <div className="pane-scroll min-h-0 flex-1 p-4">
        {tab === 'details' && (
          <DetailsTab
            conversation={conversation}
            agents={agents}
            scheduled={scheduled}
            onViewScheduled={onViewScheduled}
          />
        )}
        {tab === 'calls' && <CallsTab />}
        {tab === 'notes' && (
          <NotesTab notes={notes} agents={agents} onAdd={onAddNote} isSaving={isSavingNote} />
        )}
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ details -- */

function DetailsTab({
  conversation, agents, scheduled, onViewScheduled,
}: Omit<Props, 'onClose' | 'notes' | 'onAddNote' | 'isSavingNote'>) {
  // Truthiness, not `!== null`: `undefined !== null` is true, so a field the API
  // stops returning would have this panel announce a block nobody made.
  const blocked = Boolean(conversation.blockedAt);
  const [editing, setEditing] = useState(false);

  return (
    <>
      {/* The identity block is left-aligned with the name beside the avatar, not
          a centred card. The panel is 290px and a centred column wastes the half
          of it that a long name needs. */}
      {/* The name is edited where it is written: the title becomes the field and
          the pencil becomes the tick that commits it. A separate form below with
          its own label and its own Save was mine, not the reference's, and it
          moved the thing being edited away from the thing being read. */}
      <NameLine
        conversation={conversation}
        editing={editing}
        onEdit={() => setEditing(true)}
        onDone={() => setEditing(false)}
      />

      {/* Not in the design being matched, and kept anyway: "blocked" is our own
          decision and the agent looking at a silent conversation needs to know it
          was us. Opt-out has its own row below, as theirs does. */}
      {blocked && (
        <p className="mt-2 rounded-(--radius-chip) bg-(--color-bad)/12 px-2.5 py-1 text-micro font-medium text-(--color-bad)">
          Blocked by us
        </p>
      )}

      <div className="mt-4 flex flex-col gap-3">
        <Field label="Name on WhatsApp" value={conversation.displayName ?? 'Not shared'} />
        <Field label="Phone number" value={`+${conversation.waId}`} />
        {/* No "Business user ID" row. The design being matched has one; we hold no
            such identifier, and a labelled row that is permanently empty is a dead
            control — forbidden here since M1. */}
        <div>
          <p className="text-small font-medium text-(--color-muted)">Contacted on</p>
          <div className="mt-1 flex items-center gap-2">
            <Avatar name={conversation.channelName} waId={conversation.channelNumber ?? '0'} size={28} />
            <span className="min-w-0">
              <span className="block truncate text-body font-medium">{conversation.channelName ?? 'This number'}</span>
              <span className="block truncate text-small text-(--color-muted) tabular-nums">
                {conversation.channelNumber ? `+${conversation.channelNumber.replace(/^\+/, '')}` : 'Not recorded'}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-col">
        <OptIn
          contactId={conversation.contactId}
          optedOutAt={conversation.optedOutAt}
        />

        <SectionRow
          icon={<IconClock />}
          label="Scheduled messages"
          trailing={
            <button
              type="button"
              onClick={onViewScheduled}
              disabled={scheduled.length === 0}
              className="text-micro font-medium text-(--color-brand) hover:underline disabled:text-(--color-muted) disabled:no-underline"
            >
              View
            </button>
          }
        >
          {scheduled.length > 0 && (
            <p className="text-small text-(--color-muted)">
              {scheduled.length} waiting · next {formatIst(scheduled[0]?.scheduledFor)}
            </p>
          )}
        </SectionRow>

        <Collaborators conversationId={conversation.id} agents={agents} />

        <TagSection conversationId={conversation.id} applied={conversation.tags} />

        <CustomFields contactId={conversation.contactId} />
      </div>
    </>
  );
}

/**
 * Is this a usable contact id?
 *
 * `/api/v1/contacts/undefined` is a URL that can only ever 404, and building one
 * is always a bug on this side rather than a missing record on the server's. It
 * happened for real: the API dropped `contactId` from the conversation payload,
 * every save in this panel interpolated `undefined` into the path, and the agent
 * was told "Not Found" — which reads as *your contact is missing* rather than
 * *we never asked about a contact*.
 *
 * So nothing here fires a request without one, and the controls that need it say
 * why they are unavailable. It does not paper over the server bug — the panel is
 * visibly reduced, and `tools/check-api-shape.mjs` fails loudly — it just stops
 * an internal fault being reported to an agent as a fact about their customer.
 */
const usableId = (id: unknown): id is number =>
  typeof id === 'number' && Number.isInteger(id) && id > 0;

/* ----------------------------------------------------------------- opt-in -- */

/**
 * The consent record, and — since the reference makes it one — a control.
 *
 * It was read-only here at first, on the grounds that consent is the customer's
 * decision rather than a setting. That reasoning was half right and the result was
 * a tick that looked like a control and did nothing, which is worse than either
 * option. The two directions are not symmetrical, so they are not treated alike:
 *
 * - **Opting out is immediate.** Somebody has asked not to be messaged and an
 *   agent is recording it. Making that take two clicks is friction on the one
 *   action there is never a reason to discourage.
 * - **Opting back in asks first.** That is a claim that the customer gave consent,
 *   and it is the claim a DPDP complaint turns on. The confirm is not ceremony —
 *   it is the difference between a decision and a mis-click, and the server stamps
 *   who did it either way.
 */
function OptIn({ contactId, optedOutAt }: { contactId: number; optedOutAt: string | null }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `null` is a customer who has not opted out. `undefined` is an API that did
  // not tell us either way, and the two must not render the same: collapsing them
  // with Boolean() is what showed a green tick — "happy to receive messages" —
  // against people who had opted out. Not knowing is a third state and it says so.
  const unknown = optedOutAt === undefined;
  const optedOut = Boolean(optedOutAt);

  const change = useMutation({
    mutationFn: (optIn: boolean) =>
      optIn ? inboxApi.optIn(contactId) : inboxApi.optOut(contactId),
    onSuccess: () => {
      setConfirming(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not save.'),
  });

  return (
    <SectionRow
      icon={<IconCheckSquare />}
      label="Opt-in"
      trailing={
        <button
          type="button"
          role="switch"
          aria-checked={unknown ? 'mixed' : !optedOut}
          disabled={change.isPending || unknown || !usableId(contactId)}
          title={usableId(contactId) ? undefined
            : 'The server did not send a contact id with this conversation, so this cannot be changed.'}
          aria-label={optedOut ? 'Record that this customer opted back in' : 'Record that this customer opted out'}
          onClick={() => (optedOut ? setConfirming((v) => !v) : change.mutate(false))}
          className={[
            'tap grid size-5 shrink-0 place-items-center rounded-sm border disabled:opacity-60',
            unknown
              ? 'border-(--color-line) bg-transparent text-(--color-muted)'
              : !optedOut
                ? 'border-(--color-brand) bg-(--color-brand) text-(--color-brand-ink)'
                : 'border-(--color-line) bg-(--color-sunken) hover:border-(--color-brand)',
          ].join(' ')}
        >
          {unknown && <span aria-hidden="true" className="text-micro leading-none">?</span>}
          {!unknown && !optedOut && (
            <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
              <path d="m2.5 6.2 2.3 2.3L9.5 3.8" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      }
    >
      {unknown && (
        <p className="text-small text-(--color-muted)">
          Not known — the server did not send this customer&rsquo;s opt-in state.
        </p>
      )}

      {!unknown && optedOut && !confirming && (
        <p className="text-small text-(--color-muted)">Opted out on {formatIst(optedOutAt)}</p>
      )}

      {!unknown && optedOut && confirming && (
        <div className="rounded-(--radius-panel) border border-(--color-line) bg-(--color-sunken) p-2.5">
          <p className="text-small">
            Record that this customer has asked to receive messages again?
          </p>
          <p className="mt-1 text-micro text-(--color-muted)">
            Only if they actually said so. This is the consent record a complaint is
            answered from, and your name goes on it.
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="tap rounded px-2 py-1 text-small text-(--color-muted) hover:text-(--color-ink)"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={change.isPending}
              onClick={() => change.mutate(true)}
              className="tap rounded bg-(--color-brand) px-3 py-1 text-small font-medium text-(--color-brand-ink) disabled:opacity-60"
            >
              Opt back in
            </button>
          </div>
        </div>
      )}

      {error && <p role="alert" className="mt-1.5 text-micro text-(--color-bad)">{error}</p>}
    </SectionRow>
  );
}

/* ------------------------------------------------------- the compact rows -- */

/**
 * One row of the details list: an icon, a label, an optional hint behind an (i),
 * and one control on the right. Anything the row reveals renders underneath.
 *
 * The design being matched shows these collapsed — a label and a `+` — and that
 * is the point of them. A side panel that renders every editor open is a wall of
 * controls, and the reader is here to read a conversation.
 */
function SectionRow({ icon, label, hint, trailing, children }: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  trailing?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <section className="border-t border-(--color-line) py-3 first:border-t-0 first:pt-0">
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="shrink-0 text-(--color-muted)">{icon}</span>
        <h3 className="min-w-0 flex-1 text-small font-semibold tracking-wide text-(--color-ink) uppercase">
          {label}
        </h3>
        {hint && (
          // `title` is the tooltip; the text is also in the accessible name, so a
          // screen reader gets the explanation rather than the word "info".
          <span
            title={hint}
            aria-label={`${label}: ${hint}`}
            tabIndex={0}
            className="grid size-4 shrink-0 place-items-center rounded-full border border-(--color-line) text-[9px] font-semibold text-(--color-muted)"
          >
            i
          </span>
        )}
        {trailing}
      </div>
      {children && <div className="mt-2">{children}</div>}
    </section>
  );
}

function PlusButton({ label, expanded, onClick }: { label: string; expanded: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-label={label}
      className="tap grid size-6 shrink-0 place-items-center rounded text-(--color-brand) hover:bg-(--color-brand-wash)"
    >
      <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
        <path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      {/* 12/18 at 500 over 14 at 500 — the reference's label/value pair. */}
      <p className="text-small font-medium text-(--color-muted)">{label}</p>
      <p className="text-body font-medium break-words">{value}</p>
    </div>
  );
}

const IconCheckSquare = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <rect x="3" y="3" width="14" height="14" rx="3" />
    <path d="m6.8 10.2 2.1 2.1 4.3-4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconClock = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="10" cy="10" r="7.2" />
    <path d="M10 5.8V10l2.8 1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconPeople = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <circle cx="7.6" cy="7" r="2.6" />
    <path d="M2.8 16c0-2.4 2.1-4 4.8-4s4.8 1.6 4.8 4" strokeLinecap="round" />
    <path d="M13.4 5.2a2.4 2.4 0 0 1 0 4.6M14.4 12.4c1.7.5 2.8 1.8 2.8 3.6" strokeLinecap="round" />
  </svg>
);
const IconTag = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M3.2 9.4V3.8h5.6l7.7 7.7-5.6 5.6z" strokeLinejoin="round" />
    <circle cx="6.6" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);
const IconFields = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M7.5 3.4H5.2A1.8 1.8 0 0 0 3.4 5.2v9.6a1.8 1.8 0 0 0 1.8 1.8h2.3M12.5 3.4h2.3a1.8 1.8 0 0 1 1.8 1.8v9.6a1.8 1.8 0 0 1-1.8 1.8h-2.3" strokeLinecap="round" />
    <path d="M10 6v8" strokeLinecap="round" />
  </svg>
);

/* --------------------------------------------------------- editing a name -- */

/**
 * Avatar, name, and one control on the right that is a pencil until you press it
 * and a tick afterwards. Enter commits, Escape abandons.
 *
 * The field holds the name we keep, not the one WhatsApp reports — those are two
 * different facts about one person and the rows below show both. The placeholder
 * is the number, which is what the title falls back to when we hold no name.
 */
function NameLine({ conversation, editing, onEdit, onDone }: {
  conversation: Conversation;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(conversation.displayName ?? '');
  const [error, setError] = useState<string | null>(null);

  // A different conversation is a different name; without this the field keeps
  // whatever was typed against the last one.
  useEffect(() => {
    setDraft(conversation.displayName ?? '');
    setError(null);
  }, [conversation.contactId, conversation.displayName]);

  const save = useMutation({
    mutationFn: () => inboxApi.updateContact(conversation.contactId, { displayName: draft.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['contact', conversation.contactId] });
      onDone();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not save.'),
  });

  return (
    <div>
      <div className="flex items-center gap-2.5">
        <Avatar name={conversation.displayName} waId={conversation.waId} size={40} />

        {editing ? (
          <input
            autoFocus
            value={draft}
            maxLength={200}
            placeholder={conversation.waId}
            aria-label="Contact name"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); save.mutate(); }
              if (event.key === 'Escape') { setDraft(conversation.displayName ?? ''); onDone(); }
            }}
            className="tap min-w-0 flex-1 rounded border border-(--color-line) bg-(--color-surface) px-2.5 py-2 text-body outline-none focus:border-(--color-brand)"
          />
        ) : (
          <h3 className="min-w-0 flex-1 text-lead font-semibold break-words">
            {conversation.displayName ?? `+${conversation.waId}`}
          </h3>
        )}

        <button
          type="button"
          disabled={save.isPending || !usableId(conversation.contactId)}
          title={usableId(conversation.contactId) ? undefined
            : 'The server did not send a contact id with this conversation, so the name cannot be saved.'}
          onClick={() => (editing ? save.mutate() : onEdit())}
          aria-label={editing ? 'Save this name' : 'Edit this contact'}
          className={[
            'tap grid size-8 shrink-0 place-items-center rounded disabled:opacity-60',
            editing
              ? 'text-(--color-brand) hover:bg-(--color-brand-wash)'
              : 'text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)',
          ].join(' ')}
        >
          {editing ? (
            <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m4.5 10.5 3.6 3.6L15.8 6" />
            </svg>
          ) : (
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor"
                 strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M13.4 3.6a1.7 1.7 0 0 1 2.4 2.4L7.3 14.5l-3.2.8.8-3.2z" />
            </svg>
          )}
        </button>
      </div>

      {error && <p role="alert" className="mt-1.5 text-micro text-(--color-bad)">{error}</p>}
    </div>
  );
}

/* --------------------------------------------------------- custom fields -- */

/**
 * The values a client keeps against a customer — order number, city, plan.
 *
 * The `+` opens a searchable list of the fields that are not on this contact yet;
 * choosing one adds a row you type into. Each row is the field name, an underlined
 * value, a tick to commit and a bin to clear it. That shape is the reference's and
 * it is the right one for a panel this narrow: five fields as five labelled boxes
 * is a form, and this is not a form, it is a place to record one thing while
 * somebody is talking to you.
 *
 * The definitions themselves are the tenant's and live in Settings. A tenant with
 * none gets a sentence naming where they are made, not a plus that does nothing.
 */
function CustomFields({ contactId }: { contactId: number }) {
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [added, setAdded] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  const known = usableId(contactId);
  const fields = useQuery<ContactField[]>({
    queryKey: ['contact-fields'], queryFn: () => inboxApi.contactFields(), enabled: known,
  });
  const contact = useQuery({
    queryKey: ['contact', contactId], queryFn: () => inboxApi.contact(contactId), enabled: known,
  });

  useEffect(() => { setAdded([]); setPicking(false); }, [contactId]);

  useEffect(() => {
    if (!picking) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setPicking(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPicking(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [picking]);

  const values: Record<string, string> = (() => {
    const raw = contact.data?.attributes;
    if (!raw) return {};
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
    } catch { return {}; }
  })();

  const save = useMutation({
    mutationFn: (patch: Record<string, string | null>) =>
      inboxApi.updateContact(contactId, { attributes: patch }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
      void queryClient.invalidateQueries({ queryKey: ['conversation-events'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'That did not save.'),
  });

  const defined = fields.data ?? [];
  // Shown: anything with a value, plus anything added in this sitting. The second
  // half is what lets you start typing into a field that is still empty.
  const shown = defined.filter((f) => (values[f.fieldKey] ?? '') !== '' || added.includes(f.fieldKey));
  const addable = defined
    .filter((f) => !shown.some((x) => x.id === f.id))
    .filter((f) => f.label.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div ref={root} className="relative">
      <SectionRow
        icon={<IconFields />}
        label="Custom fields"
        hint="Values your team keeps against a customer. The fields themselves are defined in Settings."
        trailing={known ? (
          <PlusButton
            label="Add a custom field"
            expanded={picking}
            onClick={() => { setSearch(''); setPicking((v) => !v); }}
          />
        ) : undefined}
      >
        {!known && (
          <p className="text-small text-(--color-muted)">
            Unavailable — the server did not send a contact id with this conversation.
          </p>
        )}

        {known && shown.length === 0 && !picking && (
          <p className="text-small text-(--color-muted)">
            {defined.length === 0 ? 'No fields defined yet — they are set up in Settings.' : 'Nothing recorded.'}
          </p>
        )}

        {known && shown.length > 0 && (
          <div className="flex flex-col gap-3">
            {shown.map((field) => (
              <FieldValue
                key={field.id}
                field={field}
                value={values[field.fieldKey] ?? ''}
                busy={save.isPending}
                onSave={(next) => save.mutate({ [field.fieldKey]: next === '' ? null : next })}
                onClear={() => {
                  setAdded((list) => list.filter((k) => k !== field.fieldKey));
                  if ((values[field.fieldKey] ?? '') !== '') save.mutate({ [field.fieldKey]: null });
                }}
              />
            ))}
          </div>
        )}

        {error && <p role="alert" className="mt-1.5 text-micro text-(--color-bad)">{error}</p>}
      </SectionRow>

      {picking && (
        <div className="absolute right-0 z-30 mt-1 w-[15rem] overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) shadow-(--shadow-pop)">
          <input
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search custom field"
            aria-label="Search custom field"
            className="tap w-full border-b border-(--color-line) bg-(--color-surface) px-3 py-2.5 text-small outline-none placeholder:text-(--color-muted)"
          />
          <div className="max-h-56 overflow-y-auto py-1">
            {addable.length === 0 && (
              <p className="px-3 py-3 text-small text-(--color-muted)">
                {defined.length === 0
                  ? 'None defined. An administrator creates them in Settings.'
                  : 'Nothing left to add.'}
              </p>
            )}
            {addable.map((field) => (
              <button
                key={field.id}
                type="button"
                onClick={() => {
                  setAdded((list) => [...list, field.fieldKey]);
                  setPicking(false);
                }}
                className="tap block w-full px-3 py-2 text-left text-small hover:bg-(--color-sunken)"
              >
                {field.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One field: its name, an underlined value, a tick and a bin.
 *
 * The tick is what commits — not a blur handler. Blur fires when an agent clicks
 * anywhere, including on the message they were reading, so a save-on-blur field
 * writes a half-typed value every time attention moves.
 */
function FieldValue({ field, value, busy, onSave, onClear }: {
  field: ContactField;
  value: string;
  busy: boolean;
  onSave: (next: string) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const dirty = draft.trim() !== value;

  return (
    <div>
      <p className="text-small font-medium text-(--color-brand)">{field.label}</p>
      <div className="flex items-center gap-1.5">
        <input
          value={draft}
          disabled={busy}
          maxLength={400}
          aria-label={field.label}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') { event.preventDefault(); onSave(draft.trim()); }
            if (event.key === 'Escape') setDraft(value);
          }}
          className="focus-underline min-w-0 flex-1 rounded-none border-x-0 border-t-0 border-b border-(--color-line) bg-transparent px-0.5 py-1 text-small outline-none focus:border-b-2 focus:border-(--color-brand) disabled:opacity-60"
        />
        <button
          type="button"
          disabled={busy || !dirty}
          onClick={() => onSave(draft.trim())}
          aria-label={`Save ${field.label}`}
          className="tap grid size-6 shrink-0 place-items-center rounded text-(--color-brand) hover:bg-(--color-brand-wash) disabled:opacity-30"
        >
          <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m4.5 10.5 3.6 3.6L15.8 6" />
          </svg>
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onClear}
          aria-label={`Clear ${field.label}`}
          className="tap grid size-6 shrink-0 place-items-center rounded text-(--color-muted) hover:text-(--color-bad) disabled:opacity-40"
        >
          <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor"
               strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 6h12M8 6V4.5h4V6M6.5 6l.6 9.2a1 1 0 0 0 1 .8h3.8a1 1 0 0 0 1-.8L13.5 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- tags -- */

function TagSection({ conversationId, applied }: { conversationId: number; applied: AppliedTag[] }) {
  const [open, setOpen] = useState(false);
  return (
    <SectionRow
      icon={<IconTag />}
      label="Tags"
      hint="Your own labels for finding conversations later. They are never shown to the customer."
      trailing={<PlusButton label="Add a tag" expanded={open} onClick={() => setOpen((v) => !v)} />}
    >
      <TagEditor conversationId={conversationId} applied={applied} open={open} onOpenChange={setOpen} />
    </SectionRow>
  );
}

/* ------------------------------------------------------------ collaborators -- */

/**
 * People pulled in to look, without the conversation being reassigned.
 *
 * A deactivated collaborator renders greyed rather than disappearing: removing
 * them silently would rewrite who was involved, and who was involved is the
 * question this list exists to answer.
 */
function Collaborators({ conversationId, agents }: { conversationId: number; agents: Agent[] }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['collaborators', conversationId],
    queryFn: () => conversationApi.collaborators(conversationId),
  });

  const invalidate = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['collaborators', conversationId] });
    void queryClient.invalidateQueries({ queryKey: ['conversation-events', conversationId] });
  };

  const fail = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'That did not save.');

  const add = useMutation({
    mutationFn: (agentId: number) => conversationApi.addCollaborator(conversationId, agentId),
    onSuccess: () => { setAdding(false); invalidate(); },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (agentId: number) => conversationApi.removeCollaborator(conversationId, agentId),
    onSuccess: invalidate,
    onError: fail,
  });

  const current: Collaborator[] = list.data ?? [];
  const addable = agents.filter((a) => !current.some((c) => c.agentId === a.id));

  return (
    <SectionRow
      icon={<IconPeople />}
      label="Collaborators"
      hint="People watching this conversation without it being reassigned to them."
      trailing={
        <PlusButton
          label="Add a collaborator"
          expanded={adding}
          onClick={() => { setError(null); setAdding((v) => !v); }}
        />
      }
    >
      {current.length === 0 && !adding && (
        <p className="text-small text-(--color-muted)">Nobody else is watching this.</p>
      )}

      {current.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {current.map((person) => (
            <li key={person.agentId} className="flex items-center gap-2">
              <Avatar name={person.displayName} waId={String(person.agentId)} size={24} />
              <span className={`min-w-0 flex-1 truncate text-small ${person.isActive ? '' : 'text-(--color-muted) line-through'}`}>
                {person.displayName}
              </span>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(person.agentId)}
                className="tap grid size-5 shrink-0 place-items-center rounded text-(--color-muted) hover:text-(--color-bad) disabled:opacity-50"
                aria-label={`Remove ${person.displayName}`}
              >
                <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                  <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <select
          autoFocus
          value=""
          disabled={add.isPending || addable.length === 0}
          onChange={(event) => event.target.value && add.mutate(Number(event.target.value))}
          className="tap mt-1.5 w-full rounded-md border border-(--color-line) bg-(--color-sunken) px-2.5 py-1.5 text-small outline-none focus:border-(--color-brand) disabled:opacity-60"
        >
          <option value="">{addable.length === 0 ? 'Everybody is already here' : 'Choose somebody…'}</option>
          {addable.map((agent) => (
            <option key={agent.id} value={agent.id}>{agent.displayName}</option>
          ))}
        </select>
      )}

      {/* The refusal that matters: a collaborator on a channel they may not work.
          The API says so in a sentence; showing it here is the only way the agent
          learns why the person they picked did not appear. */}
      {error && <p role="alert" className="mt-1.5 text-micro text-(--color-bad)">{error}</p>}
    </SectionRow>
  );
}

/* -------------------------------------------------------------------- calls -- */

function CallsTab() {
  return (
    <div className="flex flex-col items-center gap-3 px-2 py-8 text-center">
      <span aria-hidden="true" className="grid size-12 place-items-center rounded-full bg-(--color-sunken) text-(--color-muted)">
        <svg viewBox="0 0 24 24" width="22" height="22">
          <path
            d="M6.5 3.5h3l1.5 4-2 1.4a12 12 0 0 0 6.1 6.1l1.4-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.5 5.7a2 2 0 0 1 2-2.2z"
            fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"
          />
        </svg>
      </span>
      <p className="text-body font-medium">Calling is not switched on</p>
      {/* Named, not vague. "Coming soon" tells an agent nothing and tells whoever
          is deciding whether to chase Meta even less. */}
      <p className="max-w-[24rem] text-small text-(--color-muted)">
        WhatsApp calling needs this number to hold a 2,000-a-day messaging limit.
        Ours is below that, so Meta will not enable it yet. The limit rises on its
        own as delivery quality holds.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------- notes -- */

function NotesTab({
  notes, agents, onAdd, isSaving,
}: { notes: Note[]; agents: Agent[]; onAdd: Props['onAddNote']; isSaving: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-1.5 text-small font-medium">Note</p>
        <NoteComposer agents={agents} isSaving={isSaving} onSave={onAdd} />
      </div>

      {notes.length === 0 ? (
        <p className="border-t border-(--color-line) px-2 pt-6 text-center text-small text-(--color-muted)">
          No internal notes on this conversation yet.
        </p>
      ) : (
        <ul className="flex flex-col">
          {notes.map((note) => <NoteRow key={note.id} note={note} />)}
        </ul>
      )}
    </div>
  );
}

/**
 * One note, with the menu the design being matched puts on it: Edit and Delete.
 *
 * The only interesting decision here is the "edited" marker. Editing a note is a
 * reasonable thing to want — it is a message between colleagues, not to a
 * customer, and fixing a wrong order number beats deleting and retyping. But a
 * note gets quoted at handover and occasionally in a dispute about what the
 * company knew and when, and an edit that leaves no trace makes every note in the
 * system read as though it always said what it says now. The server stamps
 * `edited_at` (migration 0031) and this shows it. The reference does not.
 */
function NoteRow({ note }: { note: Note }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!menu) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setMenu(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['messages'] });
  };
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : 'That did not save.');

  const edit = useMutation({
    mutationFn: () => inboxApi.editNote(note.id, draft.trim()),
    onSuccess: () => { setEditing(false); refresh(); },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => inboxApi.deleteNote(note.id),
    onSuccess: refresh,
    onError: fail,
  });

  // The server refuses anyone else's note; hiding the menu is so nobody is
  // offered a control that will fail, not the check itself.
  const mine = user?.id === note.agentId;

  return (
    <li ref={root} className="relative border-b border-(--color-line) py-3 last:border-b-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-small font-semibold">{note.agentName ?? 'Somebody'}</p>
          <p className="text-micro text-(--color-note-strong)">
            {formatIstLongDate(note.createdAt)}, {formatIstTime(note.createdAt)}
            {note.editedAt && (
              <span title={`Edited ${formatIst(note.editedAt)}`} className="text-(--color-muted)">
                {' · edited'}
              </span>
            )}
          </p>
        </div>

        {mine && (
          <button
            type="button"
            onClick={() => setMenu((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menu}
            aria-label="Note actions"
            className="tap grid size-6 shrink-0 place-items-center rounded text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
          >
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <circle cx="10" cy="4.2" r="1.4" fill="currentColor" />
              <circle cx="10" cy="10" r="1.4" fill="currentColor" />
              <circle cx="10" cy="15.8" r="1.4" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>

      {menu && (
        <div
          role="menu"
          className="absolute right-0 top-8 z-20 w-40 overflow-hidden rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => { setMenu(false); setDraft(note.body); setEditing(true); }}
            className="tap block w-full px-3 py-2 text-left text-small hover:bg-(--color-sunken)"
          >
            Edit note
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={remove.isPending}
            onClick={() => { setMenu(false); remove.mutate(); }}
            className="tap block w-full px-3 py-2 text-left text-small text-(--color-bad) hover:bg-(--color-sunken) disabled:opacity-60"
          >
            Delete note
          </button>
        </div>
      )}

      {editing ? (
        <form
          className="mt-2"
          onSubmit={(event) => { event.preventDefault(); edit.mutate(); }}
        >
          <textarea
            autoFocus
            rows={3}
            value={draft}
            maxLength={2000}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Escape') setEditing(false); }}
            aria-label="Edit this note"
            className="tap w-full resize-y rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small outline-none focus:border-(--color-brand)"
          />
          <div className="mt-1.5 flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} className="tap rounded px-2 py-1 text-small text-(--color-muted) hover:text-(--color-ink)">
              Cancel
            </button>
            <button
              type="submit"
              disabled={edit.isPending || draft.trim() === ''}
              className="tap rounded bg-(--color-brand) px-3 py-1 text-small font-medium text-(--color-brand-ink) disabled:opacity-60"
            >
              Save
            </button>
          </div>
        </form>
      ) : (
        <p className="mt-1.5 text-small break-words whitespace-pre-wrap text-(--color-note-strong)">
          {note.body}
        </p>
      )}

      {error && <p role="alert" className="mt-1.5 text-micro text-(--color-bad)">{error}</p>}
    </li>
  );
}

/* ----------------------------------------------------------------- plumbing -- */


